import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import AddDependentForm from './components/AddDependentForm';
import RelationshipCard from './components/RelationshipCard';
import ConnectionMethodsInfo from './components/ConnectionMethodsInfo';
import caregiverService from '../../services/caregiverService';
import { logger } from '../../utils/logger';
import FeatureGateModal from '../feature-gate-modal';
import subscriptionService from '../../services/subscriptionService';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';
import { trackCaregiverInvite } from '../../hooks/useGoogleAnalytics';

const UserRelationshipManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [managedProfiles, setManagedProfiles] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showFeatureGate, setShowFeatureGate] = useState(false);
  const [featureAccess, setFeatureAccess] = useState({ hasAccess: true, limit: 0, used: 0 });

  useEffect(() => {
    loadManagedProfiles();
  }, []);

  const loadManagedProfiles = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await caregiverService?.getManagedProfiles();
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load managed profiles');
        logger?.error('Failed to load managed profiles', fetchError);
      } else {
        setManagedProfiles(data || []);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Profile load error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAddForm = async () => {
    // Check managed profiles limit against plan
    const limits = await subscriptionService?.getPlanLimits();
    const maxProfiles = limits?.managed_profiles || limits?.managedProfiles || 0;

    // Only block if there is a positive hard limit AND the user has reached it
    if (maxProfiles > 0 && managedProfiles?.length >= maxProfiles) {
      setFeatureAccess({
        hasAccess: false,
        limit: maxProfiles,
        used: managedProfiles?.length
      });
      setShowFeatureGate(true);
      return;
    }

    // Show add form — free plan users with no hard limit can always add
    setShowAddForm(true);
  };

  const handleAddDependent = async (formData) => {
    setAddingProfile(true);
    setError('');
    setSuccessMessage('');

    try {
      const { data, error: addError } = await caregiverService?.createManagedProfile({
        name: formData?.name,
        relationship: formData?.relationship,
        age: formData?.age ? parseInt(formData?.age) : null,
        dateOfBirth: formData?.dateOfBirth || null,
        gender: formData?.gender || null,
        bloodGroup: formData?.bloodGroup || null
      });

      if (addError) {
        setError(addError?.message || 'Failed to add dependent user');
        logger?.error('Failed to add dependent', addError);
      } else {
        setSuccessMessage(`${formData?.name} has been added successfully`);
        setShowAddForm(false);
        await loadManagedProfiles();
        trackCaregiverInvite(formData?.relationship || 'unknown');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Add dependent error', err);
    } finally {
      setAddingProfile(false);
    }
  };

  const handleUpgrade = (planType) => {
    navigate('/subscription-management');
  };

  const handleEditProfile = (profile) => {
    // For now, just log the action
    logger?.info('Edit profile', { profileId: profile?.id });
    // TODO: Implement edit functionality
  };

  const handleRemoveProfile = async (profile) => {
    if (!window.confirm(`Are you sure you want to remove ${profile?.name}? This action cannot be undone.`)) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      const { error: removeError } = await caregiverService?.deleteManagedProfile(profile?.id);
      if (removeError) {
        setError(removeError?.message || 'Failed to remove dependent user');
        logger?.error('Failed to remove dependent', removeError);
      } else {
        setSuccessMessage(`${profile?.name} has been removed successfully`);
        await loadManagedProfiles();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Remove dependent error', err);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/caregiver-dashboard');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>User Relationship Management - MyBodyToday</title>
          <meta name="description" content="Manage dependent user relationships and permissions" />
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center pb-20">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={40} className="text-primary animate-spin" strokeWidth={2} />
            <p className="text-muted-foreground">Loading relationships...</p>
          </div>
        </div>
        <BottomTabNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>User Relationship Management - MyBodyToday</title>
        <meta name="description" content="Manage dependent user relationships and permissions" />
      </Helmet>

      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToDashboard}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                aria-label="Back to dashboard"
              >
                <Icon name="ArrowLeft" size={24} strokeWidth={2} />
              </button>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                  Relationship Management
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Manage dependent user connections and access permissions
                </p>
              </div>
              <div className="flex items-center gap-2">
                <LogoutButton showLabel={false} ariaLabel="Sign out from relationship management" />
                <CloseButton
                  navigateTo="/dashboard"
                  ariaLabel="Close relationship management"
                  onClose={handleBackToDashboard}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div className="flex-1">
                <p className="text-sm text-success font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div className="flex-1">
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Add Dependent Section */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    Add Dependent User
                  </h2>
                  {!showAddForm && (
                    <button
                      onClick={handleShowAddForm}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium transition-smooth hover:bg-primary/90 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background active:scale-[0.97]"
                    >
                      <Icon name="Plus" size={18} strokeWidth={2} />
                      Add New
                    </button>
                  )}
                </div>

                {showAddForm ? (
                  <AddDependentForm
                    onSubmit={handleAddDependent}
                    onCancel={() => setShowAddForm(false)}
                    loading={addingProfile}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Click "Add New" to create a profile for a dependent user you want to manage.
                  </p>
                )}
              </div>

              {/* Existing Relationships */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    Existing Relationships
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {managedProfiles?.length} {managedProfiles?.length === 1 ? 'person' : 'people'}
                  </span>
                </div>

                {managedProfiles?.length === 0 ? (
                  <div className="bg-card rounded-xl border border-border p-8 text-center">
                    <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={2} />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No Dependent Users Yet
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Add your first dependent user to start managing their health tracking.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {managedProfiles?.map((profile) => (
                      <RelationshipCard
                        key={profile?.id}
                        profile={profile}
                        onEdit={handleEditProfile}
                        onRemove={handleRemoveProfile}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <ConnectionMethodsInfo />

              {/* Security Notice */}
              <div className="mt-6 bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Icon name="Lock" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    Privacy & Security
                  </h3>
                </div>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    All dependent user data is encrypted and securely stored. Only you have access to their health information.
                  </p>
                  <p>
                    You can view symptom history, create entries on their behalf, and export summaries for medical appointments.
                  </p>
                  <p>
                    Dependent users can be removed at any time, and their data will be permanently deleted from your access.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Medical Disclaimer */}
          <div className="mt-8 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  <strong>Medical Disclaimer:</strong> This tool is for tracking purposes only and does not provide medical advice, diagnosis, or treatment recommendations. Always consult qualified healthcare professionals for medical concerns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomTabNavigation />

      <FeatureGateModal
        isOpen={showFeatureGate}
        onClose={() => setShowFeatureGate(false)}
        featureName="caregiver_management"
        featureDescription="Caregiver & Dependent Management"
        currentUsage={featureAccess?.used}
        limit={featureAccess?.limit}
        onUpgrade={handleUpgrade}
      />
    </>
  );
};

export default UserRelationshipManagement;